import { useState, type FormEvent } from 'react';
import { useAppDispatch } from '../../../Redux/hooks';
import { saveStudent } from '../../../Redux/studentsSlice';
import { useTranslation } from '../../../i18n/useTranslation';
import { studentDisplayName } from '../../../Utils/DisplayNameUtil';
import type { StudentModel } from '../../../Models/StudentModel';
import { StudentKind } from '../../../Models/enums';
import { ApiError } from '../../../Models/ApiModel';
import { Modal } from '../../SharedArea/Modal/Modal';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import { SlotEditor } from './SlotEditor';
import { AbsenceEditor } from './AbsenceEditor';
import { ContactEditor } from './ContactEditor';
import './StudentEditor.css';

interface StudentEditorProps {
  /** null creates a new card; a student edits that one. */
  student: StudentModel | null;
  onClose: () => void;
}

/**
 * The student card, and the organization card, which is the same card.
 *
 * The name is composed from first plus last, exactly as the server composes
 * it, so the two never drift. Changing them renames the student, and the API
 * carries their lessons, slots, contacts, availability, absences and group
 * membership across in one transaction.
 *
 * The English spelling beside each field is display only and takes no part in
 * that composition. Filling it in must never rename anybody, because a rename
 * moves every row they own.
 *
 * An organization is a body that pays without being taught, so the form drops
 * what does not apply to one: it has a name rather than a first and last, and
 * no door code, no weekly hour and no absences. What it keeps is everything
 * that makes it possible to collect from it.
 */
export function StudentEditor({ student, onClose }: StudentEditorProps) {
  const { tr, language } = useTranslation();
  const dispatch = useAppDispatch();

  const [kind, setKind] = useState(student?.kind ?? StudentKind.Student);
  const [firstName, setFirstName] = useState(student?.firstName ?? '');
  const [lastName, setLastName] = useState(student?.lastName ?? '');
  const [firstNameEn, setFirstNameEn] = useState(student?.firstNameEn ?? '');
  const [lastNameEn, setLastNameEn] = useState(student?.lastNameEn ?? '');
  const [price, setPrice] = useState(student?.price ?? '');
  const [phone, setPhone] = useState(student?.phone ?? '');
  const [address, setAddress] = useState(student?.address ?? '');
  const [addressEn, setAddressEn] = useState(student?.addressEn ?? '');
  const [entryCode, setEntryCode] = useState(student?.entryCode ?? '');
  const [notes, setNotes] = useState(student?.notes ?? '');
  const [notesEn, setNotesEn] = useState(student?.notesEn ?? '');
  const [archived, setArchived] = useState(Boolean(student?.archived));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const organization = kind === StudentKind.Organization;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await dispatch(saveStudent({
        ...(student ? { name: student.name } : {}),
        draft: {
          kind,
          firstName: firstName.trim() || null,
          // An organization has one name, held in the first-name field so the
          // server composes exactly what was typed.
          lastName: organization ? null : (lastName.trim() || null),
          firstNameEn: firstNameEn.trim() || null,
          lastNameEn: organization ? null : (lastNameEn.trim() || null),
          price: price.trim(),
          phone: phone.trim() || null,
          address: address.trim() || null,
          addressEn: addressEn.trim() || null,
          entryCode: organization ? null : (entryCode.trim() || null),
          notes: notes.trim() || null,
          notesEn: notesEn.trim() || null,
          archived: archived ? 1 : 0
        }
      })).unwrap();
      onClose();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={student ? tr('editStudentTitle') : tr('addStudent')}
      {...(student ? { subtitle: studentDisplayName(student, language) } : {})}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ghost-button" onClick={onClose}>{tr('cancel')}</button>
          <button type="submit" form="student-editor" className="primary-button" disabled={busy}>
            {busy ? <Spinner inline /> : tr('save')}
          </button>
        </>
      }
    >
      <form id="student-editor" className="student-editor" onSubmit={submit}>
        <label className="field is-wide">
          <span>{tr('kindLabel')}</span>
          <select
            className="text-input"
            value={kind}
            onChange={e => setKind(e.target.value as StudentKind)}
          >
            <option value={StudentKind.Student}>{tr('kindStudent')}</option>
            <option value={StudentKind.Organization}>{tr('kindOrganization')}</option>
          </select>
        </label>

        {organization && <p className="editor-locked is-wide">{tr('kindOrganizationHint')}</p>}

        <label className="field">
          <span>{organization ? tr('kindOrganization') : tr('firstName')}</span>
          <input className="text-input" value={firstName} onChange={e => setFirstName(e.target.value)} />
        </label>
        <label className="field">
          <span>{organization ? tr('ensembleNameEn') : tr('firstNameEn')}</span>
          <input className="text-input" dir="ltr" value={firstNameEn} onChange={e => setFirstNameEn(e.target.value)} />
        </label>

        {!organization && (
          <>
            <label className="field">
              <span>{tr('lastName')}</span>
              <input className="text-input" value={lastName} onChange={e => setLastName(e.target.value)} />
            </label>
            <label className="field">
              <span>{tr('lastNameEn')}</span>
              <input className="text-input" dir="ltr" value={lastNameEn} onChange={e => setLastNameEn(e.target.value)} />
            </label>
          </>
        )}

        <label className="field">
          <span>{tr('price')}</span>
          {/* inputMode numeric rather than type=number: the field takes whole
              shekels, and a spinner that offers decimals invites one. */}
          <input
            className="text-input"
            inputMode="numeric"
            value={price}
            onChange={e => setPrice(e.target.value.replace(/\D/g, ''))}
            required
          />
        </label>
        <label className="field">
          <span>{tr('phone')}</span>
          <input className="text-input" type="tel" value={phone} onChange={e => setPhone(e.target.value)} />
        </label>

        <label className="field">
          <span>{tr('address')}</span>
          <input className="text-input" value={address} onChange={e => setAddress(e.target.value)} />
        </label>
        <label className="field">
          <span>{tr('addressEn')}</span>
          <input className="text-input" dir="ltr" value={addressEn} onChange={e => setAddressEn(e.target.value)} />
        </label>

        {!organization && (
          <label className="field">
            <span>{tr('entryCode')}</span>
            <input className="text-input" value={entryCode} onChange={e => setEntryCode(e.target.value)} />
          </label>
        )}

        <label className="field is-wide">
          <span>{tr('notes')}</span>
          <textarea className="text-input" rows={3} value={notes} onChange={e => setNotes(e.target.value)} />
        </label>
        <label className="field">
          <span>{tr('notesEn')}</span>
          <textarea className="text-input" dir="ltr" rows={3} value={notesEn} onChange={e => setNotesEn(e.target.value)} />
        </label>

        <p className="editor-locked is-wide">{tr('spellingHint')}</p>

        <label className="check-label is-wide">
          <input type="checkbox" checked={archived} onChange={e => setArchived(e.target.checked)} />
          <span>{tr('archived')}</span>
        </label>
        {error && <p className="student-editor-error" role="alert">{tr('errorGeneric')}</p>}
      </form>

      {/*
        Outside the form, and only for a card that exists. Every section below
        saves against endpoints keyed by the name, so there is nothing to save
        them against until the card has one. Keeping them out of the <form>
        also stops their buttons from submitting it.

        An organization keeps its contacts, because somebody in the office has
        to be chased for the money, but not a weekly hour or an absence: it is
        not taught, so it has neither.
      */}
      {student
        ? (
          <>
            {student.kind !== StudentKind.Organization && (
              <>
                <SlotEditor student={student} />
                <AbsenceEditor student={student} />
              </>
            )}
            <ContactEditor student={student} />
          </>
        )
        : <p className="editor-locked is-wide">{tr('editorExtrasHint')}</p>}
    </Modal>
  );
}
